import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { Helmet } from "react-helmet-async";
import { 
  CheckCircle, 
  Phone, 
  FileText,
  MapPin,
  Star,
  Users,
  MessageSquare,
  HelpCircle,
  Home as HomeIcon,
  ArrowRight,
  AlertCircle,
  Link as LinkIcon,
  BookOpen,
  Image,
  Layers
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const LandscapingWebsiteContent = () => {
  const faqs = [
    {
      question: "How much do landscaping projects typically cost?",
      answer: "Costs vary widely by project type. Patios range from $15–$50/sq ft, retaining walls $25–$75/sq ft, and full landscape designs from $5,000–$50,000+. On your website, list 'starting at' pricing or use a quote form."
    },
    {
      question: "How long does a landscaping project take?",
      answer: "Small projects (plantings, mulching) take 1–2 days. Patios and retaining walls typically 3–7 days. Full yard transformations may take 2–4 weeks. Include typical timelines on each service page."
    },
    {
      question: "What's included in landscape design services?",
      answer: "Design services typically include site consultation, concept drawings, plant selection, and material specifications. Clarify if design is included in install pricing or charged separately."
    },
    {
      question: "Do you offer maintenance after installation?",
      answer: "Many landscapers offer maintenance packages. If you do, create a separate maintenance page and link it from your install service pages."
    },
    {
      question: "What areas do you serve?",
      answer: "List your service area clearly. Create dedicated city pages so people searching 'landscaping in [city]' find you in local results."
    },
    {
      question: "How do I get a quote for my project?",
      answer: "Explain your process: free on-site consultation, phone estimate, or online form. Make it clear what information you need upfront."
    },
    {
      question: "Are you licensed and insured?",
      answer: "Yes—mention your contractor license (if applicable) and liability insurance on your About page, footer, and service pages."
    },
    {
      question: "Can I see examples of your work?",
      answer: "Absolutely. Your gallery page should showcase before/after photos organized by project type with captions explaining the scope."
    },
    {
      question: "What materials do you use?",
      answer: "List material options (pavers, natural stone, concrete) on service pages. This helps qualify leads and builds trust."
    },
    {
      question: "Do you handle permits?",
      answer: "If you handle permits for retaining walls, structures, or grading, say so. It's a selling point for high-ticket projects."
    }
  ];

  const contentMistakes = [
    {
      problem: "No project gallery or photos",
      fix: "Add a categorized gallery with before/after shots. Photos sell high-ticket work."
    },
    {
      problem: "Generic service pages with no detail",
      fix: "Each service page needs 400+ words covering process, materials, timelines, and FAQs."
    },
    {
      problem: "No city or area targeting",
      fix: "Create city-specific pages to rank for '[service] in [city]' searches."
    },
    {
      problem: "Quote form doesn't ask project type",
      fix: "Add a project type dropdown so you can prioritize and qualify leads faster."
    },
    {
      problem: "No proof above the fold",
      fix: "Show star rating, project count, or years in business in your hero section."
    },
    {
      problem: "Huge uncompressed gallery images",
      fix: "Use WebP, lazy load, and compress images. Slow sites lose mobile visitors."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.leafrushmarketing.com/landscaping-website-content/#faq",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "description": "Done-for-you websites for lawn care and landscaping companies that drive calls, quotes, and recurring clients."
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.leafrushmarketing.com/landscaping-website-content/#webpage",
    "name": "Landscaping Website Content Guide | Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com/landscaping-website-content/",
    "description": "What to write on a landscaping website: homepage, service pages, city pages, galleries, reviews, and quote forms—plus copy formulas and examples.",
    "isPartOf": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    }
  };

  const lastUpdated = "December 2024";

  return (
    <>
      <SEO 
        title="Landscaping Website Content Guide"
        description="What to write on a landscaping website: homepage, service pages, city pages, galleries, reviews, and quote forms—plus copy formulas and examples."
        keywords="landscaping website content, what to put on a landscaping website, landscaping website copy, hardscaping website content, patio installation service page content"
        canonical="https://www.leafrushmarketing.com/landscaping-website-content/"
        ogUrl="https://www.leafrushmarketing.com/landscaping-website-content/"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      </Helmet>

      <div className="flex flex-col">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="container px-4 md:px-6 relative z-10">
            <div className="mx-auto max-w-4xl text-center space-y-6">
              <Badge variant="secondary" className="mb-4">
                Content Guide
              </Badge>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Landscaping Website Content: What to Put on Each Page
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                Exactly what to write for your landscaping, hardscaping, or design/build website—from patios and retaining walls to maintenance services.
              </p>
              
              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <a href="#get-started">
                  <Button size="lg" className="w-full sm:w-auto text-lg px-8">
                    <Phone className="mr-2 h-5 w-5" />
                    Get Started
                  </Button>
                </a>
                <NavLink to="/websites">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8">
                    See Website Subscription Pricing
                  </Button>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Answer Box */}
        <section className="pb-12">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <Card className="bg-primary/5 border-primary/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-primary" />
                    Quick Answer: 5 Must-Have Content Elements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">Hero + CTA above the fold</strong> — service type + city + phone number visible immediately</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">Dedicated service pages</strong> — patios, retaining walls, plantings each get their own page</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">Service area pages</strong> — city-specific pages for local SEO rankings</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">Project gallery</strong> — categorized before/after photos with captions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">Quote form with project type</strong> — name, phone, city, project type dropdown</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* 8 Must-Have Pages Overview */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">
                The 8 Must-Have Pages for Your Landscaping Website
              </h2>
              <p className="text-muted-foreground text-center mb-8 max-w-2xl mx-auto">
                Every landscaping, hardscaping, or design/build website needs these pages to rank and convert.
              </p>
              
              <div className="grid gap-4">
                {[
                  { icon: HomeIcon, title: "Home", purpose: "First impression + CTA. Service type + city headline, trust signals, links to services." },
                  { icon: Layers, title: "Services Hub", purpose: "Overview of all services with links to individual service pages." },
                  { icon: FileText, title: "Individual Service Pages", purpose: "Deep pages for patios, retaining walls, planting, lighting, etc." },
                  { icon: MapPin, title: "Service Areas", purpose: "City-specific pages for 'landscaping in [city]' searches." },
                  { icon: Image, title: "Gallery / Projects", purpose: "Categorized before/after photos that prove your quality." },
                  { icon: Star, title: "Reviews / Testimonials", purpose: "Build trust with project-specific reviews and star ratings." },
                  { icon: Users, title: "About", purpose: "Your story, specialties, team, and what clients can expect." },
                  { icon: MessageSquare, title: "Contact", purpose: "Phone, project quote form, service area, hours." }
                ].map((page, index) => (
                  <div key={index} className="flex items-start gap-4 bg-background rounded-lg p-4 border">
                    <page.icon className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold">{page.title}</h3>
                      <p className="text-sm text-muted-foreground">{page.purpose}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Page-by-Page Content Templates */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-4xl font-bold text-center mb-4">
                Page-by-Page Content Templates
              </h2>
              <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
                Exactly what landscaping website content goes on each page, with copy formulas and examples.
              </p>

              {/* Homepage Content */}
              <div className="mb-12" id="homepage-content">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <HomeIcon className="h-6 w-6 text-primary" />
                      1. Homepage Content
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-2">Goal of this page:</h4>
                      <p className="text-muted-foreground">Make a strong first impression, showcase your best work, and drive visitors to request a quote.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold mb-2">Above the fold checklist:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Headline with service type + city (e.g., "Landscaping & Hardscaping in Denver, CO")</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Subhead with outcome (e.g., "Transform your outdoor space with custom patios, walls, and plantings")</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Click-to-call phone number</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>"Get a Free Quote" button</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Trust signals (licensed, insured, years in business, project count)</span>
                        </li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Headline formulas you can use:</h4>
                      <div className="bg-accent/30 rounded-lg p-4 space-y-3">
                        <div>
                          <p className="font-medium text-sm text-primary">Landscaping + City:</p>
                          <p className="text-muted-foreground">"Professional Landscaping & Hardscaping in [City], [State]"</p>
                        </div>
                        <div>
                          <p className="font-medium text-sm text-primary">Project Outcome + Timeframe:</p>
                          <p className="text-muted-foreground">"Your Dream Patio Installed in 2 Weeks — Free Design Consultation"</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">6-section homepage layout:</h4>
                      <ol className="space-y-2 text-muted-foreground list-decimal list-inside">
                        <li><strong className="text-foreground">Hero:</strong> Headline, subhead, CTA, phone number, hero image of your best project</li>
                        <li><strong className="text-foreground">Trust strip:</strong> Licensed, insured, years in business, 5-star reviews</li>
                        <li><strong className="text-foreground">Services overview:</strong> 4–6 service categories with links to individual pages</li>
                        <li><strong className="text-foreground">Gallery highlight:</strong> 3–4 of your best project photos</li>
                        <li><strong className="text-foreground">Reviews:</strong> 2–3 testimonials with project context</li>
                        <li><strong className="text-foreground">Final CTA:</strong> "Ready to start your project?" with form or call button</li>
                      </ol>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Photos to add:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Hero image: Your best completed project (patio, outdoor living space, etc.)</li>
                        <li>• Before/after shots of transformations</li>
                        <li>• Detail shots showing quality craftsmanship</li>
                        <li>• You or your crew working on a project</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Internal links to include:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Link to services hub or each service page</li>
                        <li>• Link to gallery/projects page</li>
                        <li>• Link to service areas</li>
                        <li>• Link to contact/quote form</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Services Hub Content */}
              <div className="mb-12" id="services-hub-content">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <Layers className="h-6 w-6 text-primary" />
                      2. Services Hub Page Content
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-2">Goal of this page:</h4>
                      <p className="text-muted-foreground">Give visitors an overview of all services and guide them to the specific service page they need.</p>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Above the fold checklist:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>H1: "Our Landscaping & Hardscaping Services"</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Brief intro (2–3 sentences)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Visual grid or list of service categories</span>
                        </li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">How to list services without thin content:</h4>
                      <div className="bg-accent/30 rounded-lg p-4">
                        <p className="text-muted-foreground text-sm mb-3">
                          Don't just list service names. For each service on this hub page, include:
                        </p>
                        <ul className="space-y-1 text-muted-foreground text-sm">
                          <li>• Service name + icon or thumbnail</li>
                          <li>• 2–3 sentence description</li>
                          <li>• "Learn More" link to the full service page</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Example service categories:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Patio Installation</li>
                        <li>• Retaining Walls</li>
                        <li>• Landscape Design</li>
                        <li>• Planting & Softscaping</li>
                        <li>• Outdoor Lighting</li>
                        <li>• Drainage Solutions</li>
                        <li>• Maintenance Programs</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Internal links to include:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Link to each individual service page</li>
                        <li>• Link to gallery (showcase all service types)</li>
                        <li>• Link to contact/quote form</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Individual Service Page Content */}
              <div className="mb-12" id="service-page-content">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <FileText className="h-6 w-6 text-primary" />
                      3. Individual Service Page (Patio Installation Example)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-2">Goal of this page:</h4>
                      <p className="text-muted-foreground">Explain this specific service in depth, answer common questions, and convert visitors searching for this service.</p>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Above the fold checklist:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>H1: "Patio Installation in [City]"</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Hero image of a completed patio project</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Phone number + "Get a Quote" button</span>
                        </li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Service page structure:</h4>
                      <ol className="space-y-2 text-muted-foreground list-decimal list-inside">
                        <li><strong className="text-foreground">What it is:</strong> 2–3 sentences explaining patio installation</li>
                        <li><strong className="text-foreground">What's included:</strong> Bullet list (design, excavation, base prep, paver installation, sealing)</li>
                        <li><strong className="text-foreground">Materials/options:</strong> Pavers, natural stone, concrete, stamped concrete</li>
                        <li><strong className="text-foreground">Process:</strong> Consultation → design → prep → install → finish</li>
                        <li><strong className="text-foreground">Typical pricing:</strong> "$15–$50/sq ft depending on materials" or "Request quote"</li>
                        <li><strong className="text-foreground">Timeline:</strong> "Most patios completed in 3–7 days"</li>
                        <li><strong className="text-foreground">FAQs:</strong> 3–5 questions specific to patio installation</li>
                        <li><strong className="text-foreground">CTA:</strong> "Ready to build your patio?" + form or call button</li>
                      </ol>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">"What's included" example (patio):</h4>
                      <div className="bg-accent/30 rounded-lg p-4">
                        <ul className="space-y-1 text-muted-foreground">
                          <li>• Free on-site design consultation</li>
                          <li>• Excavation and grading</li>
                          <li>• Compacted gravel base with proper drainage</li>
                          <li>• Paver or stone installation with edge restraints</li>
                          <li>• Polymeric sand jointing</li>
                          <li>• Final sealing (optional)</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Photos to add:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Before/after of patio installations</li>
                        <li>• Close-ups of paver patterns and edges</li>
                        <li>• Different material options</li>
                        <li>• Crew working on installation</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Internal links to include:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Link to related services (retaining walls, outdoor lighting)</li>
                        <li>• Link to patio gallery section</li>
                        <li>• Link to service areas</li>
                        <li>• Link to contact page</li>
                      </ul>
                      <p className="text-sm text-muted-foreground mt-2">
                        Learn more about writing effective service pages: <NavLink to="/service-pages-that-rank" className="text-primary hover:underline">Service Pages That Rank</NavLink>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Service Area Page Content */}
              <div className="mb-12" id="service-area-content">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <MapPin className="h-6 w-6 text-primary" />
                      4. Service Area Page Content
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-2">Goal of this page:</h4>
                      <p className="text-muted-foreground">Rank for "landscaping in [city]" searches and show locals you work in their area.</p>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Above the fold checklist:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>H1: "Landscaping & Hardscaping in [City], [State]"</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Intro mentioning the city and your services there</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>CTA: Call or request a quote</span>
                        </li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">How to rank without looking spammy:</h4>
                      <p className="text-muted-foreground mb-3">
                        Create individual pages for main service areas. Add unique content about projects in that area, local considerations, and photos from that city.
                      </p>
                      <div className="bg-accent/30 rounded-lg p-4">
                        <p className="font-medium text-sm text-primary mb-2">Example paragraph:</p>
                        <p className="text-muted-foreground text-sm">
                          "We've been designing and installing patios, retaining walls, and custom landscapes throughout [City] and the surrounding communities for over 10 years. From the established neighborhoods near downtown to the newer developments in [Suburb], we understand the local soil, drainage, and HOA requirements."
                        </p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Photos to add:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Projects completed in that city</li>
                        <li>• Recognizable local context if appropriate</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Internal links to include:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Link to all service pages</li>
                        <li>• Link to contact/quote form</li>
                        <li>• Link to other nearby service area pages</li>
                      </ul>
                      <p className="text-sm text-muted-foreground mt-2">
                        Learn more: <NavLink to="/service-area-pages-local-seo" className="text-primary hover:underline">Service Area Pages for Local SEO</NavLink>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Gallery / Projects Page Content */}
              <div className="mb-12" id="gallery-content">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <Image className="h-6 w-6 text-primary" />
                      5. Gallery / Projects Page Content
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-2">Goal of this page:</h4>
                      <p className="text-muted-foreground">Showcase your best work and help visitors visualize what's possible for their property.</p>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Above the fold checklist:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>H1: "Our Work" or "Project Gallery"</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Filter/category tabs (Patios, Retaining Walls, Plantings, etc.)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>CTA to get a quote</span>
                        </li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">How to categorize projects:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Patios & Outdoor Living</li>
                        <li>• Retaining Walls</li>
                        <li>• Outdoor Lighting</li>
                        <li>• Plantings & Softscape</li>
                        <li>• Full Yard Transformations</li>
                        <li>• Drainage & Grading</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Caption template (problem → solution → result → location):</h4>
                      <div className="bg-accent/30 rounded-lg p-4">
                        <p className="text-muted-foreground text-sm">
                          <strong className="text-foreground">Hillside Paver Patio — [City]</strong><br />
                          "This sloped backyard was unusable. We installed a 400 sq ft paver patio with a retaining wall and built-in seating. Now the homeowners have a functional outdoor living space perfect for entertaining."
                        </p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Internal links to include:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Link each category to its service page (patio gallery → patio service page)</li>
                        <li>• Link to contact/quote form</li>
                        <li>• Link to reviews</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Reviews Page Content */}
              <div className="mb-12" id="reviews-content">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <Star className="h-6 w-6 text-primary" />
                      6. Reviews / Testimonials Page Content
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-2">Goal of this page:</h4>
                      <p className="text-muted-foreground">Build trust with social proof from past clients, especially for high-ticket projects.</p>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Above the fold checklist:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>H1: "What Our Clients Say"</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Star rating summary (e.g., "4.9 stars from 75+ reviews")</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>CTA to request a quote</span>
                        </li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Adding project-type context:</h4>
                      <div className="bg-accent/30 rounded-lg p-4">
                        <p className="text-muted-foreground text-sm">
                          For each review, include the project type:<br /><br />
                          <strong className="text-foreground">"They transformed our backyard with a beautiful paver patio and fire pit."</strong><br />
                          — John D., [City] (Patio Installation)
                        </p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Where to place high-ticket reviews:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Feature 2–3 on the homepage</li>
                        <li>• Add project-specific reviews on each service page</li>
                        <li>• Full collection on the reviews page</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Internal links to include:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Link to services mentioned in reviews</li>
                        <li>• Link to gallery</li>
                        <li>• Link to contact/quote form</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* About Page Content */}
              <div className="mb-12" id="about-content">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <Users className="h-6 w-6 text-primary" />
                      7. About Page Content
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-2">Goal of this page:</h4>
                      <p className="text-muted-foreground">Build trust by showing who's behind the company and what makes you different.</p>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Framework:</h4>
                      <ol className="space-y-2 text-muted-foreground list-decimal list-inside">
                        <li><strong className="text-foreground">Who you are:</strong> Owner name, company history, why you started</li>
                        <li><strong className="text-foreground">Specialties:</strong> Design/build focus, hardscaping, specific project types</li>
                        <li><strong className="text-foreground">How you communicate:</strong> Response times, project updates, point of contact</li>
                        <li><strong className="text-foreground">What to expect:</strong> Process from consultation to completion</li>
                      </ol>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Copy you can use:</h4>
                      <div className="bg-accent/30 rounded-lg p-4 space-y-3">
                        <p className="text-muted-foreground text-sm">
                          "I'm [Name], and I founded [Company] in [Year] to bring high-quality design/build landscaping to homeowners who want more than just plants—they want outdoor living spaces."
                        </p>
                        <p className="text-muted-foreground text-sm">
                          "We specialize in patios, retaining walls, and complete yard transformations. Every project is managed by [me/our project manager], so you'll always know who to call."
                        </p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Photos to add:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Photo of the owner</li>
                        <li>• Team photo</li>
                        <li>• Branded trucks/equipment</li>
                        <li>• You on a job site with a client</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Internal links to include:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Link to services</li>
                        <li>• Link to gallery</li>
                        <li>• Link to reviews</li>
                        <li>• Link to contact</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Contact Page Content */}
              <div className="mb-12" id="contact-content">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <MessageSquare className="h-6 w-6 text-primary" />
                      8. Contact + Quote Form Content
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-2">Goal of this page:</h4>
                      <p className="text-muted-foreground">Capture leads with a form that qualifies project type and makes follow-up easy.</p>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Above the fold checklist:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>H1: "Get a Free Quote" or "Start Your Project"</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Phone number (click-to-call)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                          <span>Quote form</span>
                        </li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Recommended form fields:</h4>
                      <div className="bg-accent/30 rounded-lg p-4">
                        <ul className="space-y-1 text-muted-foreground">
                          <li>1. Name</li>
                          <li>2. Phone</li>
                          <li>3. City/ZIP</li>
                          <li>4. Project Type (dropdown: Patio, Retaining Wall, Landscape Design, etc.)</li>
                          <li>5. Budget Range (optional dropdown: Under $5k, $5k–$15k, $15k–$30k, $30k+)</li>
                          <li>6. Brief description (optional textarea)</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Response time expectations:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• "We respond to all project inquiries within 24 hours."</li>
                        <li>• "Call or text for the fastest response."</li>
                        <li>• "Submit your project details and we'll schedule a free consultation."</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Other info to include:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Service area list or link</li>
                        <li>• Business hours</li>
                        <li>• Email (optional)</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Common Content Mistakes */}
        <section className="py-16 md:py-20 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">
                Common Content Mistakes (And How to Fix Them)
              </h2>
              <p className="text-muted-foreground text-center mb-10 max-w-2xl mx-auto">
                Avoid these landscaping website content mistakes that hurt your rankings and conversions.
              </p>

              <div className="grid gap-4">
                {contentMistakes.map((item, index) => (
                  <Card key={index}>
                    <CardContent className="p-4 flex flex-col md:flex-row gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-semibold text-sm">Problem:</p>
                          <p className="text-muted-foreground text-sm">{item.problem}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 flex-1">
                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-semibold text-sm">Fix:</p>
                          <p className="text-muted-foreground text-sm">{item.fix}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Internal Linking Map */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 justify-center mb-4">
                <LinkIcon className="h-6 w-6 text-primary" />
                <h2 className="text-2xl md:text-3xl font-bold text-center">
                  Internal Linking Map for Your Site
                </h2>
              </div>
              <p className="text-muted-foreground text-center mb-10 max-w-2xl mx-auto">
                How your landscaping website pages should connect for SEO and user flow.
              </p>

              <Card>
                <CardContent className="p-6 space-y-6">
                  <div>
                    <h4 className="font-semibold mb-3">Page connection flow:</h4>
                    <div className="bg-accent/30 rounded-lg p-4">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="bg-background px-3 py-1 rounded-full border font-medium">Home</span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        <span className="bg-background px-3 py-1 rounded-full border font-medium">Services Hub</span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        <span className="bg-background px-3 py-1 rounded-full border font-medium">Service Pages</span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        <span className="bg-background px-3 py-1 rounded-full border font-medium">City Pages</span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        <span className="bg-background px-3 py-1 rounded-full border font-medium">Contact</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">Gallery → Service page connections:</h4>
                    <ul className="space-y-1 text-muted-foreground text-sm">
                      <li>• Patio gallery section → links to Patio Installation service page</li>
                      <li>• Retaining wall photos → links to Retaining Walls service page</li>
                      <li>• Each gallery category links to its corresponding service</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">Every page should link to:</h4>
                    <ul className="space-y-1 text-muted-foreground text-sm">
                      <li>• At least one service page</li>
                      <li>• Contact or quote form</li>
                      <li>• Gallery (where relevant)</li>
                    </ul>
                  </div>

                  <div className="pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      <strong className="text-foreground">Related guides:</strong>{" "}
                      <NavLink to="/service-pages-that-rank" className="text-primary hover:underline">Service Pages That Rank</NavLink>
                      {" | "}
                      <NavLink to="/service-area-pages-local-seo" className="text-primary hover:underline">Service Area Pages for Local SEO</NavLink>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-20 bg-primary/5">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Want Us to Write and Build This Into Your Site?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                We handle the content, design, gallery setup, and SEO—so you can focus on building outdoor spaces.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="#get-started">
                  <Button size="lg" className="w-full sm:w-auto text-lg px-8">
                    <Phone className="mr-2 h-5 w-5" />
                    Get Started
                  </Button>
                </a>
                <NavLink to="/contact">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8">
                    Contact Us
                  </Button>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* Calendly */}
        <CalendlyEmbed />

        {/* Related Reading */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 justify-center mb-8">
                <BookOpen className="h-6 w-6 text-primary" />
                <h2 className="text-2xl md:text-3xl font-bold text-center">
                  Related Reading
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { title: "Landscaping Website Design", description: "See what's included in our done-for-you landscaping websites.", href: "/landscaping-website-design" },
                  { title: "Landscaping Website Cost", description: "Understand pricing options for landscaping websites.", href: "/landscaping-website-cost" },
                  { title: "Service Pages That Rank", description: "How to structure service pages for SEO.", href: "/service-pages-that-rank" },
                  { title: "Service Area Pages for Local SEO", description: "Rank in the cities where you work.", href: "/service-area-pages-local-seo" },
                  { title: "Website Subscription Pricing", description: "Our month-to-month website packages.", href: "/websites" },
                  { title: "Google Business Profile Optimization", description: "Get more calls from local search.", href: "/growth-add-ons/gbp" },
                  { title: "Free Resources", description: "Guides and checklists for lawn and landscaping businesses.", href: "/resources" },
                  { title: "Lawn Care Website Content Guide", description: "If you mainly do mowing/maintenance, see this guide.", href: "/lawn-care-website-content" }
                ].map((item, index) => (
                  <NavLink key={index} to={item.href}>
                    <Card className="h-full hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <h3 className="font-semibold mb-1">{item.title}</h3>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      </CardContent>
                    </Card>
                  </NavLink>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 md:py-20 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Frequently Asked Questions
              </h2>
              
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* Last Updated */}
        <section className="py-8 border-t">
          <div className="container px-4 md:px-6">
            <p className="text-center text-sm text-muted-foreground">
              Last updated: {lastUpdated}
            </p>
          </div>
        </section>
      </div>
    </>
  );
};

export default LandscapingWebsiteContent;
